import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import moment from 'moment-timezone';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import AutoSelect from 'forms/AdminBulkOrderForm/AutoSelect';
import { elderPaymentMethod } from '@silvergatedelivery/constants';
import LabelWithToolTip from '../LabelWithToolTip';
import cache from 'utilities/cache';

import {
  counties,
  TIME_ZONE,
} from '@silvergatedelivery/constants';
import { asyncListAll, request } from 'utilities/graph';

import issueReceipts from './issueReceipts';

import {
  getEldersByClientByStatus,
  getOrdersByClientByDate,
  getPandagoOrdersByOrderByCreatedAt,
  getElder,
} from '../queries';
import { getClient } from 'graphql/queries';

import { processShareOrder } from '../CountyPrsReport/workbooks/helpers';

const monthOptions = Array(10).fill('').map((x, index) => {
  return moment().add(-index, 'months').format('YYYY-MM');
});

export default function SelfPayingReceipt({ clientOptions = [] }) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]);
  const [selectedCounty, setSelectedCounty] = useState(counties[0]);
  const [selectedClientId, setSelectedClientId] = useState();
  const [includeUndeliveredOrders, setIncludeUndeliveredOrders] = useState(false);
  // const [excludeNonFoodDeliveryOrders, setExcludeNonFoodDeliveryOrders] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('all');
  const [groupByDistrict, setGroupByDistrict] = useState(true);
  const excludeNonFoodDeliveryOrders = true;
  const cacheKey = 'app:report:selfPayingReceipt:uiState';

  const updateCache = (newState = {}) => {
    cache.set(cacheKey, {
      includeUndeliveredOrders,
      selectedPaymentMethod,
      groupByDistrict,
      ...newState,
    });
  };

  const collectData = async () => {
    const from = moment(selectedMonth).tz(TIME_ZONE).startOf('month');
    const to = moment(selectedMonth).tz(TIME_ZONE).endOf('month');
    const fromDate = from.format('YYYY-MM-DD');
    const toDate = to.format('YYYY-MM-DD');
    const fromISOString = from.toISOString();
    const toISOString = to.toISOString();
    // const monthString = from.format('YYYY/MM');
    const period = `${fromDate}__${toDate}`;
    const { id: clientId } = clientOptions.find(({ id }) => id === selectedClientId);

    global.logger.debug({
      selectedClientId,
      period,
      selectedMonth,
      fromDate,
      toDate,
      fromISOString,
      toISOString,
    });

    let [
      elders,
      orders,
      { data: { getClient: client } },
    ] = await Promise.all([
      asyncListAll(getEldersByClientByStatus, {
        clientId,
        status: { eq: '使用中' },
        limit: 1000,
      }),
      asyncListAll(getOrdersByClientByDate, {
        clientId,
        date: {
          between: [fromDate, toDate],
        },
        limit: 1000,
      }),
      request(getClient, { id: clientId }),
    ]);


    const missingElderIds = [...new Set(orders
      .map(({ elderId }) => elderId)
      .filter((elderId) => !elders.find(({ id }) => id === elderId)))];
    if (missingElderIds.length > 0) {
      const missingElders = await Promise.all(
        missingElderIds.map(async (id) => {
          const { data: { getElder: data } } = await request(getElder, { id });
          return data;
        }),
      );
      elders.push(...missingElders);
    }
    elders = elders.filter(({ isDisadvantaged }) => !isDisadvantaged);

    const selfPayingOrders = orders.filter(({ elderId }) => elders.findIndex(({ id }) => id === elderId) !== -1);

    const deliveryStaffIds = [];
    selfPayingOrders.forEach(({ deliveryStaffId }) => {
      if (deliveryStaffId && !deliveryStaffIds.includes(deliveryStaffId)) {
        deliveryStaffIds.push(deliveryStaffId);
      }
    });

    let pandagoDeliveryStaffId;
    const deliveryStaffs = await Promise.all(deliveryStaffIds.map(async (id) => {
      const { data: { getDeliveryStaff: data } } = await request( /* GraphQL */ `
        query GetDeliveryStaff($id: ID!) {
          getDeliveryStaff(id: $id) {
            id
            name
            username
            identificationCardId
          }
        }
      `, { id });

      if (data.username === 'pandago') {
        pandagoDeliveryStaffId = id;
      }

      return data;
    }));

    const pandagoOrders = await Promise.all(selfPayingOrders
      .filter(({ deliveryStaffId }) => deliveryStaffId === pandagoDeliveryStaffId)
      .map(async ({ id: orderId }) => {
        const [pandagoOrder] = await asyncListAll(getPandagoOrdersByOrderByCreatedAt, {
          orderId,
          limit: 1,
        });

        return pandagoOrder;
      }));

    processShareOrder(elders, selfPayingOrders);

    issueReceipts({
      county: selectedCounty,
      elders,
      orders: selfPayingOrders,
      deliveryStaffs, pandagoOrders, selectedMonth, client, includeUndeliveredOrders, excludeNonFoodDeliveryOrders,
      selectedPaymentMethod, groupByDistrict,
    });
  };

  const triggerDownload = async () => {
    try {
      setIsLoading(true);
      await collectData();
    } catch (e) {
      global.logger.debug(e);
    } finally {
      setIsLoading(false);
    }
  };


  const updateCounty = (client) => {
    if (client && client.county) {
      const targetCounty = counties.find((county) => county === client.county);
      if (targetCounty) {
        setSelectedCounty(targetCounty);
      }
    }
  };

  useEffect(() => {
    if (clientOptions[0]) {
      setSelectedClientId(clientOptions[0].id);
      updateCounty(clientOptions[0]);
    }
    const cached = cache.get(cacheKey);
    if (cached) {
      setIncludeUndeliveredOrders(!!cached.includeUndeliveredOrders);
      setSelectedPaymentMethod(cached.selectedPaymentMethod || 'all');
      setGroupByDistrict(!!cached.groupByDistrict);
    }
  }, [clientOptions]);

  return (
    <div>
      <h3>餐飲服務費收據(自費戶)</h3>
      <div>
        <FormControl style={{ minWidth: 360, marginRight: 16 }}>
          <AutoSelect
            id="selected-restaurant"
            options={clientOptions.map(({ name }) => name)}
            values={clientOptions.map(({ id }) => id)}
            required={true}
            value={selectedClientId}
            label="機構"
            onChange={(value) => {
              setSelectedClientId(value);
              if (value) {
                const client = clientOptions.find(({ id }) => id === value);
                updateCounty(client);
              }
            }}
          />
        </FormControl>
        <FormControl style={{ width: 150, marginRight: 16 }}>
          <InputLabel id="selected-month">月份</InputLabel>
          <Select
            labelId="selected-month-label"
            id="selected-month"
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            disabled={isLoading}
          >
            {monthOptions.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ width: 150, marginRight: 16 }}>
          <InputLabel id="selected-county">縣市</InputLabel>
          <Select
            labelId="selected-county-label"
            id="selected-county"
            value={selectedCounty}
            onChange={(event) => setSelectedCounty(event.target.value)}
            disabled={isLoading}
          >
            {counties.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ width: 150, marginRight: 16 }}>
          <InputLabel id="selected-payment-method">收費方式</InputLabel>
          <Select
            labelId="selected-payment-method-label"
            id="selected-payment-method"
            value={selectedPaymentMethod}
            onChange={(event) => {
              setSelectedPaymentMethod(event.target.value);
              updateCache({ selectedPaymentMethod: event.target.value });
            }}
            disabled={isLoading}
          >
            <MenuItem key={'all'} value={'all'}>
              全部
            </MenuItem>
            {/* value是'null', 會無法顯示'無', 用'none'處理 */}
            {elderPaymentMethod.map(({ value, label }) => (
              <MenuItem key={label} value={value || 'none'}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeUndeliveredOrders}
              name={'includeUndeliveredOrders'}
              value={includeUndeliveredOrders}
              disabled={!selectedClientId || isLoading}
              onChange={(e) => {
                setIncludeUndeliveredOrders(e.target.checked);
                updateCache({ includeUndeliveredOrders: e.target.checked });
              }}
            />
          }
          label={'包含未送達的訂單'}
        />
        {/* <FormControlLabel
          control={
            <Checkbox
              checked={excludeNonFoodDeliveryOrders}
              name={'excludeNonFoodDeliveryOrders'}
              value={excludeNonFoodDeliveryOrders}
              disabled={!selectedClientId || isLoading}
              onChange={(e) => {
                setExcludeNonFoodDeliveryOrders(e.target.checked);
              }}
            />
          }
          label={'排除非送餐的訂單'}
        /> */}
        <FormControlLabel
          control={
            <Checkbox
              checked={groupByDistrict}
              name={'groupByDistrict'}
              value={groupByDistrict}
              disabled={!selectedClientId || isLoading}
              onChange={(e) => {
                setGroupByDistrict(e.target.checked);
                updateCache({ groupByDistrict: e.target.checked });
              }}
            />
          }
          label={<LabelWithToolTip
            label='以鄉鎮市區歸檔'
            tooltip='注意：如果沒有以鄉鎮市區歸檔有可能因爲檔案工作表的數量過多，造成Google試算表無法開啟'
            color='#f50057'
          />}
        />
      </div>

      <Button
        variant="outlined"
        onClick={triggerDownload}
        disabled={!selectedClientId || isLoading}
        style={{ marginTop: 16 }}
      >
        下載
        {isLoading && <CircularProgress color="primary" size={16} style={{ marginLeft: 8 }} />}
      </Button>
    </div>
  );
}

SelfPayingReceipt.propTypes = {
  clientOptions: PropTypes.array,
};
